import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import ProjectForm from "./ProjectForm";
import "./Projects.css";
import "../shared/starfield.css";

const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [activeTab, setActiveTab] = useState("all"); // 'all', 'owned', 'shared'
    const navigate = useNavigate();

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get("/projects/");
            setProjects(response.data);
        } catch (err) {
            console.error("Error fetching projects:", err);
            toast.error("Failed to fetch projects.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchInvitations = useCallback(async () => {
        try {
            const response = await API.get("/invitations/");
            setInvitations(response.data);
        } catch (err) {
            console.error("Error fetching invitations:", err);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
        fetchInvitations();
    }, [fetchProjects, fetchInvitations]);

    const handleProjectClick = (projectId) => {
        navigate(`/Projects/${projectId}`);
    };

    const handleAcceptInvitation = async (invitationId) => {
        try {
            await API.post(`/invitations/${invitationId}/accept/`);
            toast.success("Invitation accepted!");
            fetchProjects();
            fetchInvitations();
        } catch (err) {
            console.error("Error accepting invitation:", err);
            toast.error("Failed to accept invitation.");
        }
    };

    const handleDeclineInvitation = async (invitationId) => {
        try {
            await API.post(`/invitations/${invitationId}/decline/`);
            toast.success("Invitation declined.");
            fetchInvitations();
        } catch (err) {
            console.error("Error declining invitation:", err);
            toast.error("Failed to decline invitation.");
        }
    };

    const currentUsername = localStorage.getItem("username");

    const filteredProjects = projects.filter((project) => {
        if (activeTab === "owned") {
            return project.owner?.username === currentUsername;
        }
        if (activeTab === "shared") {
            return project.owner?.username !== currentUsername;
        }
        return true;
    });

    const getRoleBadge = (role) => {
        const colors = {
            owner: "bg-primary",
            admin: "bg-warning text-dark",
            editor: "bg-success",
            viewer: "bg-secondary",
        };
        return (
            <span className={`badge ${colors[role] || "bg-secondary"} ms-2`}>
                {role}
            </span>
        );
    };

    return (
        <div className="projects-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            <Navbar />
            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Projects</h2>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        + New Project
                    </button>
                </div>

                {/* Pending Invitations */}
                {invitations.length > 0 && (
                    <div className="card mb-4 border-warning">
                        <div className="card-header bg-warning text-dark">
                            <strong>Pending Invitations ({invitations.length})</strong>
                        </div>
                        <div className="card-body">
                            {invitations.map((invitation) => (
                                <div
                                    key={invitation.id}
                                    className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                >
                                    <div>
                                        <strong>{invitation.project?.name}</strong>
                                        <span className="text-muted ms-2">
                                            Invited by {invitation.invited_by?.username} as{" "}
                                            {invitation.role}
                                        </span>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-success me-2"
                                            onClick={() => handleAcceptInvitation(invitation.id)}
                                        >
                                            Accept
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDeclineInvitation(invitation.id)}
                                        >
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "all" ? "active" : ""}`}
                            onClick={() => setActiveTab("all")}
                        >
                            All Projects ({projects.length})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "owned" ? "active" : ""}`}
                            onClick={() => setActiveTab("owned")}
                        >
                            Owned Projects
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "shared" ? "active" : ""}`}
                            onClick={() => setActiveTab("shared")}
                        >
                            Shared with Me
                        </button>
                    </li>
                </ul>

                {/* Projects Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : filteredProjects.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <p>No projects found.</p>
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create your first project
                        </button>
                    </div>
                ) : (
                    <div className="row">
                        {filteredProjects.map((project) => (
                            <div key={project.id} className="col-md-4 mb-4">
                                <div
                                    className="card h-100 project-card"
                                    onClick={() => handleProjectClick(project.id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <h5 className="card-title">{project.name}</h5>
                                            {getRoleBadge(project.user_role)}
                                        </div>
                                        <p className="card-text text-muted">
                                            {project.description || "No description"}
                                        </p>
                                        <div className="d-flex gap-3 text-muted small">
                                            <span>
                                                <i className="fa fa-users me-1"></i>
                                                {project.member_count} members
                                            </span>
                                            <span>
                                                <i className="fa fa-flask me-1"></i>
                                                {project.study_count} studies
                                            </span>
                                            <span>
                                                <i className="fa fa-database me-1"></i>
                                                {project.dataset_count} datasets
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent">
                                        <small className="text-muted">
                                            Owner: {project.owner?.username}
                                            {project.visibility === "public" && (
                                                <span className="badge bg-info ms-2">Public</span>
                                            )}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Project Modal */}
                {showCreateModal && (
                    <ProjectForm
                        onClose={() => setShowCreateModal(false)}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchProjects();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default ProjectList;
